import { useCurrentPageAccess } from "@/app/router";
import { ConfirmDialog } from "@/shared/components/feedback/ConfirmDialog";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { useKeywordFilter } from "@/shared/hooks";
import { useDialog } from "@/shared/hooks/useDialog";
import { toast } from "@/shared/hooks/useToast";
import { RotateCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { UniversalThrottlingPolicyDialog } from "../components/UniversalThrottlingPolicyDialog";
import { useUniversalThrottlingColumns } from "../hooks/useUniversalThrottlingColumns";
import { useUniversalThrottlingPolicies } from "../hooks/useUniversalThrottlingPolicies";
import type { UniversalThrottlingPolicy } from "../types/preferences_types";
import type { SortingState } from "@tanstack/react-table";

export function PreferencesUniversalThrottlingPolicyPage() {
  const { policies, reload, createPolicy, updatePolicy, deletePolicies, isLoading } =
    useUniversalThrottlingPolicies();
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const columns = useUniversalThrottlingColumns();

  const [selectedRows, setSelectedRows] = useState<UniversalThrottlingPolicy[]>([]);

  const createDialog = useDialog<void>();
  const editDialog = useDialog<UniversalThrottlingPolicy>();
  const deleteDialog = useDialog<void>();

  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);

  const filter = useKeywordFilter(
    policies,
    (p) => [p.id, p.channel, p.throttlingKey, JSON.stringify(p.throttlingMethod)],
    { debounceMs: 0 }
  );

  // 검색 필터 변경 시 첫 페이지로 이동
  useEffect(() => {
    setPage(1);
  }, [filter.keyword]);

  // 정렬된 데이터
  const sortedData = useMemo(() => {
    if (sorting.length === 0) return filter.filteredRows;

    const sorted = [...filter.filteredRows];
    const sort = sorting[0];

    sorted.sort((a, b) => {
      const aValue = a[sort.id as keyof typeof a];
      const bValue = b[sort.id as keyof typeof b];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // 숫자 타입인 경우 숫자로 비교
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort.desc ? bValue - aValue : aValue - bValue;
      }

      // 객체 타입인 경우 JSON 문자열로 비교
      if (typeof aValue === 'object' || typeof bValue === 'object') {
        const aStr = JSON.stringify(aValue);
        const bStr = JSON.stringify(bValue);
        if (aStr < bStr) return sort.desc ? 1 : -1;
        if (aStr > bStr) return sort.desc ? -1 : 1;
        return 0;
      }

      // 문자열 타입인 경우 문자열로 비교
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return sort.desc ? 1 : -1;
      if (aStr > bStr) return sort.desc ? -1 : 1;
      return 0;
    });

    return sorted;
  }, [filter.filteredRows, sorting]);

  // 페이지네이션 핸들러
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // 페이지 크기 변경 시 첫 페이지로 이동
  };

  // 현재 페이지에 표시할 데이터 계산
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleSubmitCreate = async (payload: Omit<UniversalThrottlingPolicy, "id">) => {
    const res = await createPolicy(payload);
    if (!res.ok) {
      toast({
        title: "생성 실패",
        description: res.error,
        variant: "destructive",
      });
      return;
    }
    createDialog.close();
  };

  const handleSubmitEdit = async (payload: Omit<UniversalThrottlingPolicy, "id">) => {
    const target = editDialog.data;
    if (!target) return;

    const res = await updatePolicy(target.id, payload);
    if (!res.ok) {
      toast({
        title: "수정 실패",
        description: res.error,
        variant: "destructive",
      });
      return;
    }
    editDialog.close();
  };

  const handleDelete = async () => {
    await deletePolicies(selectedRows.map((r) => r.id));
    setSelectedRows([]);
    deleteDialog.close();
    // 삭제 후 데이터 새로고침
    await reload();
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader title="스로틀링 정책" description="스로틀링 정책을 관리합니다." />

      <DataTable
        key={policies.length}
        columns={columns}
        data={paginatedData}
        totalCount={sortedData.length}
        page={page}
        pageSize={pageSize}
        sorting={sorting}
        onSortingChange={setSorting}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        isLoading={isLoading}
        enableRowSelection
        rowSelectionTrigger="checkbox"
        getRowId={(row) => row.id}
        onRowSelectionChange={setSelectedRows}
        onRowClick={canWriteCurrentPage ?
          (row) => {
            editDialog.open(row);
          }
          : undefined
        }
        toolbar={
          <DataTableToolbar
            searchValue={filter.keyword}
            onSearchChange={filter.setKeyword}
            showReset={!!filter.keyword}
            onReset={filter.reset}
            actions={
              <>
                <Button variant="outline" onClick={reload}>
                  <RotateCw className="mr-2 h-4 w-4" />
                  새로고침
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    createDialog.open();
                  }}
                  disabled={!canWriteCurrentPage}
                >
                  추가
                </Button>
                <Button
                  variant="destructive"
                  disabled={!canWriteCurrentPage || selectedRows.length === 0}
                  onClick={() => deleteDialog.open()}
                >
                  삭제
                </Button>
              </>
            }
          />
        }
      />

      <UniversalThrottlingPolicyDialog
        open={createDialog.isOpen}
        onOpenChange={(open) => !open && createDialog.close()}
        mode="create"
        onSubmit={handleSubmitCreate}
      />

      <UniversalThrottlingPolicyDialog
        open={editDialog.isOpen}
        onOpenChange={(open) => !open && editDialog.close()}
        mode="edit"
        initial={editDialog.data}
        onSubmit={handleSubmitEdit}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="정책 삭제"
        description={`선택한 ${selectedRows.length}개의 정책을 삭제하시겠습니까?`}
        confirmText="삭제"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
}
